import {
    GetSessionError,
    useGetProxied,
} from '~/backend/connector-api/connector-components';
import { Session } from '~/backend/connector-api/connector-schemas';
import { UserModels } from '~/backend/types';
import { EnvironmentObjectIds } from '~/engine/data/Environment';
import useAutoSegmentationTask from '~/hooks/workflows/connector/tasks/useAutoSegmentationTask';
import { transformAvailableModelsData } from '~/utils/transforms/predictionModelsDataTransforms';
import AutoSegmentation from './AutoSegmentation';

type ContourConnectorWorkflowProps = {
    sessionData: Session | undefined;
    environmentObjectIds: EnvironmentObjectIds | undefined;
    isLoadingSession: boolean;
    error: Error | GetSessionError | null;
};

export default function ContourConnectorWorkflow({
    sessionData,
    environmentObjectIds,
    isLoadingSession,
    error,
}: ContourConnectorWorkflowProps) {
    const { start: startProcessing, isSubmitting } = useAutoSegmentationTask({
        studyId:
            environmentObjectIds?.primaryImage?.studyInstanceUID ??
            (sessionData?.payload.context_image.study_instance_uid || ''),
        seriesId:
            environmentObjectIds?.primaryImage?.seriesInstanceUID ??
            (sessionData?.payload.context_image.series_instance_uid || ''),
        patientId: sessionData?.payload.patient.id || '',
        structureSetSopInstanceUid:
            environmentObjectIds?.structureSet?.sopInstanceUID || '',
    });
    const { data: modelsData, isLoading: isLoadingModels } = useGetProxied(
        { pathParams: { rest: 'contouring/list-active-models' } },
        { refetchInterval: 10000 }
    );

    const {
        data: modelCustomizations = [],
        isLoading: isLoadingCustomizations,
    } = useGetProxied(
        { pathParams: { rest: 'config/contouring' } },
        { refetchInterval: 10000 }
    );

    const availableModels = modelsData
        ? transformAvailableModelsData(modelsData as UserModels)
        : [];

    return (
        <AutoSegmentation
            key={'auto-segmentation-modal'}
            availableModels={availableModels}
            modelCustomizations={modelCustomizations}
            onSubmit={startProcessing}
            isLoadingModels={isLoadingModels || isLoadingCustomizations}
            isLoadingSession={isLoadingSession}
            isSubmitting={isSubmitting}
            error={error}
            showPseudonymization={false}
        />
    );
}
